import type { ChangeEvent, Dispatch, SetStateAction } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { currencyMaskBRL, currencyToNumber } from "helpers/utils";
import { formatarMoedaBRL } from "../helpers/financeiro-formatters";
import {
  CONTAS_FIN_ACCENT,
  fieldSx,
  modalHeaderSx,
  modalPaperSx,
  saveButtonSx,
} from "../../components/contas-financeiras.theme";
import type {
  ConciliarSaldoContaFormPayload,
  ContaFinanceiraGerencialApi,
} from "../types/contas-financeiras.types";

export type ConciliarSaldoContaDialogState = {
  open: boolean;
  conta: ContaFinanceiraGerencialApi | null;
};

type Props = {
  state: ConciliarSaldoContaDialogState;
  form: ConciliarSaldoContaFormPayload;
  setForm: Dispatch<SetStateAction<ConciliarSaldoContaFormPayload>>;
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: () => void;
};

export function formPadraoConciliarSaldoConta(
  partial?: Partial<ConciliarSaldoContaFormPayload>,
): ConciliarSaldoContaFormPayload {
  return {
    saldoReal: "",
    observacao: "",
    ...partial,
  };
}

function previewConciliacao(
  saldoExtrato: number,
  saldoRealStr: string,
): {
  divergencia: number;
  tipo: "ENTRADA" | "SAIDA" | null;
  saldoDepois: number;
} | null {
  const saldoReal = Math.round(currencyToNumber(saldoRealStr) * 100) / 100;
  if (!saldoRealStr.trim() || !Number.isFinite(saldoReal)) {
    return null;
  }
  const divergencia = Math.round((saldoReal - saldoExtrato) * 100) / 100;
  if (Math.abs(divergencia) <= 0.01) {
    return { divergencia: 0, tipo: null, saldoDepois: saldoReal };
  }
  return {
    divergencia: Math.abs(divergencia),
    tipo: divergencia > 0 ? "ENTRADA" : "SAIDA",
    saldoDepois: saldoReal,
  };
}

export default function ConciliarSaldoContaDialog({
  state,
  form,
  setForm,
  loading,
  error,
  onClose,
  onConfirm,
}: Props) {
  const conta = state.conta;
  const saldoExtrato = conta?.saldoAtual ?? 0;
  const preview = previewConciliacao(saldoExtrato, form.saldoReal);

  const patch =
    (field: keyof ConciliarSaldoContaFormPayload) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const usarSaldoExtrato = () => {
    if (!conta) return;
    setForm((prev) => ({
      ...prev,
      saldoReal: conta.saldoAtual.toFixed(2).replace(".", ","),
    }));
  };

  return (
    <Dialog
      open={state.open}
      onClose={loading ? undefined : onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{ sx: modalPaperSx }}
      aria-labelledby="conciliar-saldo-conta-title"
    >
      <Box sx={modalHeaderSx}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box minWidth={0}>
            <Typography variant="overline" sx={{ opacity: 0.75, letterSpacing: 1.2 }}>
              Conciliação
            </Typography>
            <Typography id="conciliar-saldo-conta-title" variant="h5" fontWeight="bold">
              Ajustar saldo
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.85 }}>
              Informe o saldo real no banco ou caixa. O sistema lança a diferença no extrato interno.
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "inherit" }} aria-label="Fechar" disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>
      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={2.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <TuneOutlinedIcon sx={{ color: CONTAS_FIN_ACCENT }} />
            <MDTypography variant="button" fontWeight="bold">
              Saldo real vs extrato
            </MDTypography>
          </Stack>
          {error ? <Alert severity="error">{error}</Alert> : null}

          <Box
            sx={{
              p: 1.5,
              borderRadius: 1.5,
              border: 1,
              borderColor: "divider",
              bgcolor: "action.hover",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Conta
            </Typography>
            <Typography variant="subtitle2" fontWeight={700}>
              {conta?.nome ?? "—"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              Saldo no extrato interno:{" "}
              <Typography
                component="span"
                variant="body2"
                fontWeight={600}
                color="text.primary"
              >
                {formatarMoedaBRL(saldoExtrato)}
              </Typography>
            </Typography>
          </Box>

          <Box>
            <TextField
              label="Saldo atual no banco/caixa (R$)"
              value={currencyMaskBRL(form.saldoReal || "0")}
              onChange={patch("saldoReal")}
              fullWidth
              required
              size="small"
              helperText={
                saldoExtrato < -0.009
                  ? `Extrato interno negativo (${formatarMoedaBRL(saldoExtrato)}). Informe o saldo final desejado.`
                  : "Saldo final que a conta deve ficar após o ajuste, igual ao que consta no banco."
              }
              sx={fieldSx}
            />
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 0.75 }}>
              <MDButton
                variant="text"
                color="dark"
                size="small"
                onClick={usarSaldoExtrato}
                disabled={!conta}
                sx={{ color: CONTAS_FIN_ACCENT }}
              >
                Usar saldo do extrato
              </MDButton>
            </Stack>
          </Box>

          {preview ? (
            <Alert
              severity={
                preview.tipo === "ENTRADA"
                  ? "success"
                  : preview.tipo === "SAIDA"
                    ? "warning"
                    : "info"
              }
            >
              {preview.tipo === null ? (
                <>Nenhum ajuste necessário — saldos já estão alinhados.</>
              ) : (
                <>
                  Será lançada uma{" "}
                  <strong>{preview.tipo === "ENTRADA" ? "entrada" : "saída"}</strong> de{" "}
                  {formatarMoedaBRL(preview.divergencia)} para o extrato passar de{" "}
                  {formatarMoedaBRL(saldoExtrato)} para{" "}
                  <strong>{formatarMoedaBRL(preview.saldoDepois)}</strong> (saldo
                  informado). O valor do lançamento é a diferença entre os dois,
                  não o valor digitado acima.
                </>
              )}
            </Alert>
          ) : null}

          <TextField
            label="Observação (opcional)"
            value={form.observacao}
            onChange={patch("observacao")}
            fullWidth
            multiline
            minRows={2}
            size="small"
            placeholder="Ex.: Juros creditados pelo banco em 05/06"
            sx={fieldSx}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "grey.200" }}>
        <MDButton variant="outlined" color="dark" onClick={onClose} disabled={loading}>
          Cancelar
        </MDButton>
        <MDButton
          variant="gradient"
          color="dark"
          onClick={() => void onConfirm()}
          disabled={loading || !conta || !form.saldoReal.trim()}
          sx={saveButtonSx}
        >
          {loading ? "Ajustando…" : "Ajustar saldo"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
